// TributiView.jsx — amm-v3 (A5, ondata 32): la vista "Imposte dell'anno"
// (pagina 'tributi') in React, flag-gated (shell_amm_v3_tributi, default OFF).
// PRIMO consumatore reale di TreeView (client/src/ui) nel cantiere amm.
//
// Replica 1:1 renderTributi di admin-module.js r.6663-6781: titolo con tooltip
// tecnico, ‹ anno ›, "Perché non torna?" (se residuo), Esporta CSV, 4 KPI
// (Dovuto/Pagato/Residuo/Avanzamento) e l'ALBERO categoria → tributo → voce →
// occorrenze su TreeView (stati vuoto/errore del design system). Le espansioni
// (categoria collassata, voce multi-periodo aperta) sono stato del CHIAMANTE,
// come da contratto di TreeView — condiviso col v1 su AMM._tribCollapsed /
// AMM._tribVociExp (parità di persistenza; anno su AMM.tribAnno).
//
// CONFINE (dichiarato): editor del dovuto (openDovuto → setDovuto, con
// guardClose/busy) e box "Perché non torna?" (openQuadraturaSpiegata) restano
// V1; openDovuto riceve dal v3 il proprio onSaved (refresh v3) ⇒ mai
// renderTributi diretto (niente bypass/interceptor). Badge stato (tribStatoBadge)
// ed etichetta periodo (tribPeriodoLabel) DELEGATI al v1.
//
// DEVIAZIONI DICHIARATE:
//  D-A5.4 caricamento fallito ⇒ StatoBox errore + Riprova (nessun dato) o
//         banner + dati conservati (famiglia D-A1.5). Il v1 non cattura l'errore
//         di renderTributi (unhandled rejection): il v3 lo dichiara rumoroso.
//  D-A5.5 il DOM passa da <table> a righe flex (design system TreeView, come
//         Categorie proc-v3): la parità si misura su VALORI ed EFFETTI, non sul
//         DOM (B3b). Colonne allineate (label 1fr + 3×130px + 90px).
//  D-A5.6 Esporta CSV doppio-click = 1 richiesta (guardia ref); il v1 non guarda
//         il bottone e ne spara 2 (miglioria come A4/D-A4.2). Read, non write.

import React from 'react';
import { TreeView } from '../ui/TreeView.jsx';
import { AM_NEW_TEXTS } from './api.js';
import {
  TR_TEXTS, kpiDefs, voceMulti, buildTributiTree, treeKey,
  readTributi, apriDovuto, apriQuadratura, esportaCsv,
} from './tributi-api.js';

// messaggio di albero vuoto — testo NUOVO del v3 (il v1 mostra una tabella con
// il solo header e tbody vuoto): dichiarato, non pinnato al v1 (famiglia AM_NEW).
const TR_NEW = { emptyAlbero: 'Nessuna imposta registrata per questo anno.' };

function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function StatoBox({ error, onRetry }) {
  if (error) {
    const msg = error && error.message ? error.message : String(error);
    return (
      <div role="alert" className="bg-white border rounded-lg p-6 text-center" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
        <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{'⚠ ' + msg}</div>
        <button type="button" style={{ marginTop: 12, background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={onRetry}>{AM_NEW_TEXTS.riprova}</button>
      </div>
    );
  }
  return <div className="text-slate-400 text-sm">Caricamento…</div>;
}

// celle numeriche/stato con larghezza FISSA (allineamento colonne tra le righe)
function Num({ children, cls }) {
  return <div className={'w-[130px] shrink-0 px-3 text-right tabular-nums ' + (cls || '')}>{children}</div>;
}
function StatoCell({ children }) {
  return <div className="w-[90px] shrink-0 px-3 text-right">{children}</div>;
}

export function TributiView({ win, tick }) {
  const A = win.AMM;
  const f2 = A.fmtEur2;
  if (A.tribAnno == null) A.tribAnno = new Date().getFullYear();
  if (!A._tribCollapsed) A._tribCollapsed = {};
  if (!A._tribVociExp) A._tribVociExp = {};

  const [anno, setAnno] = React.useState(() => A.tribAnno);
  const [dati, setDati] = React.useState({ loading: true, error: null, albero: null });
  const [reloadN, setReloadN] = React.useState(0);
  const [, setForceN] = React.useState(0); // re-render dopo mutazione di collassi/espansioni (stato su AMM)
  const exportingRef = React.useRef(false); // D-A5.6: export doppio-click = 1 (il v1 ne spara 2)

  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readTributi(win, anno).then(
      (albero) => { if (vivo) setDati({ loading: false, error: null, albero }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN, anno]);

  const albero = dati.albero;
  const reload = () => setReloadN((n) => n + 1);
  const force = () => setForceN((n) => n + 1);
  const shownAnno = albero ? albero.anno : anno;
  const selezionaAnno = (a) => { A.tribAnno = a; setAnno(a); };

  const toggleCat = (categoria) => { A._tribCollapsed[categoria] = !A._tribCollapsed[categoria]; force(); };
  const toggleVoce = (codice) => { A._tribVociExp[codice] = !A._tribVociExp[codice]; force(); };
  const onExport = async () => {
    if (exportingRef.current) return; // doppio click = 1 sola richiesta (D-A5.6)
    exportingRef.current = true;
    try { await esportaCsv(win, shownAnno); }
    catch (e) { A.toast(TR_TEXTS.exportFallito + (e && e.message ? e.message : e)); }
    finally { exportingRef.current = false; }
  };

  const kpis = albero ? kpiDefs(albero, f2) : [];

  // ---- righe dell'albero (renderRow di TreeView) ----
  const renderRow = (entry) => {
    const n = entry.node;
    if (n.kind === 'cat') {
      const cat = n.cat;
      const collapsed = !!A._tribCollapsed[cat.categoria];
      return (
        <div className="amm-trib-cat flex items-center bg-slate-100 cursor-pointer text-sm" data-cat={cat.categoria} onClick={() => toggleCat(cat.categoria)}>
          <div className="flex-1 min-w-0 px-3 py-2 font-semibold text-slate-700">
            <span className="inline-block w-4 text-slate-400">{collapsed ? '▸' : '▾'}</span>{cat.categoria_label}
          </div>
          <Num>{f2(cat.dovuto)}</Num>
          <Num cls="text-emerald-700">{cat.pagato ? f2(cat.pagato) : TR_TEXTS.dash}</Num>
          <Num cls="font-medium">{cat.residuo == null ? TR_TEXTS.dash : f2(cat.residuo)}</Num>
          <StatoCell />
        </div>
      );
    }
    if (n.kind === 'trib') {
      const t = n.trib;
      return (
        <div className="flex items-center bg-slate-50/60 text-sm">
          <div className="flex-1 min-w-0 px-3 py-1.5 pl-9 text-xs uppercase tracking-wide text-slate-500 font-medium">{t.tributo}</div>
          <Num>{f2(t.dovuto)}</Num>
          <Num cls="text-emerald-700">{t.pagato ? f2(t.pagato) : TR_TEXTS.dash}</Num>
          <Num cls="font-medium">{t.residuo == null ? TR_TEXTS.dash : f2(t.residuo)}</Num>
          <StatoCell />
        </div>
      );
    }
    if (n.kind === 'voce') {
      const v = n.voce;
      const multi = voceMulti(v);
      const exp = multi && !!A._tribVociExp[v.codice];
      const onRow = () => {
        if (multi) { toggleVoce(v.codice); return; }
        apriDovuto(win, shownAnno, v, reload);
      };
      const codici = v.codici_f24 && v.codici_f24.length ? v.codici_f24.join('/') : null;
      return (
        <div className="amm-trib-voce flex items-center border-t hover:bg-cyan-50/40 cursor-pointer text-sm" data-voce={v.codice} onClick={onRow}>
          <div className="flex-1 min-w-0 px-3 py-2 pl-12">
            {multi
              ? <span className="amm-trib-chev inline-block w-4 text-slate-400 cursor-pointer" data-voce={v.codice} onClick={(e) => { e.stopPropagation(); toggleVoce(v.codice); }}>{exp ? '▾' : '▸'}</span>
              : <span className="inline-block w-4" />}
            {v.label}
            {codici ? <>{' '}<span className="text-[10px] text-slate-300 font-mono">{codici}</span></> : null}
          </div>
          <Num cls={v.dovuto == null ? 'text-slate-300' : ''}>{v.dovuto == null ? TR_TEXTS.imposta : f2(v.dovuto)}</Num>
          <Num cls="text-emerald-700">{v.pagato ? f2(v.pagato) : TR_TEXTS.dash}</Num>
          <Num cls="font-medium">{v.residuo == null ? TR_TEXTS.dash : f2(v.residuo)}</Num>
          <StatoCell><V1Html html={A.tribStatoBadge(v.stato)} /></StatoCell>
        </div>
      );
    }
    // kind === 'occ'
    const v = n.voce;
    const o = n.occ;
    const rosa = o.periodo === 'da-classificare';
    const manuale = o.dovuto_override != null;
    return (
      <div className={'amm-trib-occ flex items-center border-t hover:bg-cyan-50/40 cursor-pointer text-xs ' + (rosa ? 'bg-rose-50/50' : 'bg-slate-50/40')} data-voce={v.codice} data-periodo={o.periodo} onClick={(e) => { e.stopPropagation(); apriDovuto(win, shownAnno, v, reload, o.periodo); }}>
        <div className={'flex-1 min-w-0 px-3 py-1.5 pl-16 ' + (rosa ? 'text-rose-700' : 'text-slate-500')}>
          {A.tribPeriodoLabel(o.periodo)}
          {rosa ? <>{' '}<span className="text-[10px] text-rose-400">{TR_TEXTS.daClassificareNota}</span></> : null}
        </div>
        <Num cls={o.dovuto_effettivo == null ? 'text-slate-300' : ''}>
          {o.dovuto_effettivo == null ? TR_TEXTS.dash : f2(o.dovuto_effettivo)}
          {manuale ? <>{' '}<span className="amm-badge" style={{ background: '#fef3c7', color: '#92400e' }}>{TR_TEXTS.manuale}</span></> : null}
        </Num>
        <Num cls="text-emerald-700">{o.pagato ? f2(o.pagato) : TR_TEXTS.dash}</Num>
        <Num cls="font-medium">{o.residuo == null ? TR_TEXTS.dash : f2(o.residuo)}</Num>
        <StatoCell><V1Html html={A.tribStatoBadge(o.stato)} /></StatoCell>
      </div>
    );
  };

  const roots = albero ? buildTributiTree(albero.categorie) : [];

  return (
    <div data-amm-v3="tributi">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold" title={TR_TEXTS.titleTooltip}>{TR_TEXTS.title}</h1>
          <div className="flex items-center gap-1">
            <button id="trib-prev" type="button" className="px-2 py-1 border rounded text-sm" onClick={() => selezionaAnno(shownAnno - 1)}>{'‹'}</button>
            <span className="px-2 font-semibold tabular-nums">{shownAnno}</span>
            <button id="trib-next" type="button" className="px-2 py-1 border rounded text-sm" onClick={() => selezionaAnno(shownAnno + 1)}>{'›'}</button>
          </div>
          {albero && albero.totali.residuo
            ? <button id="amm-trib-perche" type="button" className="bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs px-3 py-2 rounded font-medium" onClick={() => apriQuadratura(win, shownAnno)}>{TR_TEXTS.perche}</button>
            : null}
          <button id="amm-trib-export" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onExport}>{TR_TEXTS.exportCsv}</button>
        </div>
        <div className="text-xs text-slate-400">{TR_TEXTS.sub}</div>
      </div>

      {/* D-A5.4: errore con dati presenti ⇒ banner + Riprova, dati conservati */}
      {dati.error && albero ? (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={reload}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      ) : null}

      {!albero ? (
        <StatoBox error={dati.error} onRetry={reload} />
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {kpis.map((k) => (
              <div key={k.label} className="bg-white border rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{k.label}</div>
                <div className="text-lg font-semibold mt-0.5" style={{ color: k.color }}>{k.val}</div>
              </div>
            ))}
          </div>
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="flex items-center bg-slate-50 text-slate-500">
              <div className="flex-1 min-w-0 px-3 py-2 text-xs uppercase font-medium">{TR_TEXTS.colTributoVoce}</div>
              <div className="w-[130px] shrink-0 px-3 py-2 text-xs uppercase font-medium text-right">{TR_TEXTS.colDovuto}</div>
              <div className="w-[130px] shrink-0 px-3 py-2 text-xs uppercase font-medium text-right">{TR_TEXTS.colPagato}</div>
              <div className="w-[130px] shrink-0 px-3 py-2 text-xs uppercase font-medium text-right">{TR_TEXTS.colResiduo}</div>
              <div className="w-[90px] shrink-0 px-3 py-2" />
            </div>
            <TreeView
              roots={roots}
              nodeKey={treeKey}
              getChildren={(node) => node.children || []}
              isExpanded={(node) => (node.kind === 'cat'
                ? !A._tribCollapsed[node.cat.categoria]
                : (node.kind === 'voce' ? (voceMulti(node.voce) && !!A._tribVociExp[node.voce.codice]) : false))}
              renderRow={renderRow}
              emptyMessage={TR_NEW.emptyAlbero}
            />
          </div>
        </>
      )}
    </div>
  );
}

// Factory (pattern createAmmV3Esercizi)
export function createAmmV3Tributi(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0 && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<TributiView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
